import React, { useEffect, useState } from 'react';
import { Card, Table, Badge, Button, Form, Alert, Modal } from 'react-bootstrap';
import axios from '../../../services/AxiosInstance';
import { useParams, useHistory } from 'react-router-dom';
import swal from "sweetalert";
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { getApiUrl } from '../../../utils/ApiUtils';
import PageTitle from '../../layouts/PageTitle';
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCalendarAlt, faSave, faPlus, faEdit, faFileExport } from '@fortawesome/free-solid-svg-icons';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Calendar, Building, Settings, Trash2, Edit, Save, Plus, FileText, TrendingUp, AlertCircle, Info } from 'lucide-react';
import sv from 'date-fns/locale/sv';
import { toast } from 'sonner';

const FiscalYearDetails = () => {
  const { companyId, fiscalYearId } = useParams();
  const [fiscalYear, setFiscalYear] = useState({});
  const [openingBalances, setOpeningBalances] = useState([]);
  const [newOpeningLiabilityBalances, setNewOpeningLiabilityBalances] = useState([]);
  const [liabilityAccountsOptions, setLiabilityAccountsOptions] = useState([]);
  const [newOpeningAssetsBalances, setNewOpeningAssetsBalances] = useState([]);
  const [assetsAccountsOptions, setAssetsAccountsOptions] = useState([]);
  const [assets, setAssets] = useState([]);
  const [totalAssets, setTotalAssets] = useState(0);
  const [totalLiabilitiesEquity, setTotalLiabilitiesEquity] = useState(0);
  const [liabilities, setLiabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [closingDate, setClosingDate] = useState(null);
  const [selectedAccountingMethod, setSelectedAccountingMethod] = useState(null);
  const [selectedVatPeriod, setSelectedVatPeriod] = useState(null);
  const history = useHistory();
  const [deletedBalances, setDeletedBalances] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isProvisional, setIsProvisional] = useState(false);

  const accountingMethodOptions = [
    { value: 'accrual', label: 'Fakturametoden' },
    { value: 'cash', label: 'Kontantmetoden' }
  ];

  const vatPeriodOptions = [
    { value: 'monthly', label: 'Varje månad' },
    { value: 'quarterly', label: 'En gång per kvartal' },
    { value: 'yearly', label: 'En gång per år' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        fetchFiscalYearDetails(),
        fetchOpeningBalances(),
        fetchAccountsOptions('Eget kapital och skulder').then(setLiabilityAccountsOptions),
        fetchAccountsOptions('Tillgångar').then(setAssetsAccountsOptions)
      ]);
    };
    fetchData();
  }, [companyId, fiscalYearId]);

  const fetchAccountsOptions = async (type) => {
    try {
      const result = await axios.get(getApiUrl(`${companyId}/accounting/accounts?accountType=${type}`), { withCredentials: true });
      return result.data.accounts.map(account => ({ value: account.account_id, label: `${account.account_number} - ${account.account_name}` }));
    } catch (error) {
      console.error('Error fetching accounts options:', error);
    }
  };

  const calculateTotalOpeningBalance = (balances) => {
    const total = balances.reduce((acc, balance) => acc + parseFloat(balance.opening_balance), 0);
    // Round to 2 decimal places to avoid floating-point precision issues
    return Math.round(total * 100) / 100;
  };

  const fetchFiscalYearDetails = async () => {
    try {
      const result = await axios.get(getApiUrl(`${companyId}/accounting/fiscalyears/${fiscalYearId}`), { withCredentials: true });
      const fiscalYearData = result.data.fiscal_year;

      setFiscalYear(fiscalYearData);
      setStartDate(new Date(fiscalYearData.start_date));
      setEndDate(new Date(fiscalYearData.end_date));
      setClosingDate(fiscalYearData.closing_date ? new Date(fiscalYearData.closing_date) : null);
      setSelectedAccountingMethod(accountingMethodOptions.find(option => option.value === fiscalYearData.accounting_method));
      setSelectedVatPeriod(vatPeriodOptions.find(option => option.value === fiscalYearData.vat_period));
    } catch (error) {
      // swal({
      //   title: "Error",
      //   text: "Failed to fetch fiscal year details",
      //   icon: "error",
      //   button: "OK",
      // });
      toast.error("Failed to fetch fiscal year details");
    }
  };

  const fetchOpeningBalances = async () => {
    try {
      const result = await axios.get(getApiUrl(`${companyId}/accounting/fiscalyears/${fiscalYearId}/opening-balances`), { withCredentials: true });
      const openingBalancesData = result.data.opening_balances;
      setIsProvisional(result.data.is_provisional || false);

      setOpeningBalances(openingBalancesData);

      const assetsBalances = openingBalancesData.filter(balance => balance.account_number < 2000);
      const liabilitiesEquityBalances = openingBalancesData.filter(balance => balance.account_number >= 2000);

      const totalAssets = calculateTotalOpeningBalance(assetsBalances);
      const totalLiabilitiesEquity = calculateTotalOpeningBalance(liabilitiesEquityBalances);

      console.log('Total Assets:', totalAssets);
      console.log('Total Liabilities & Equity:', totalLiabilitiesEquity);

      setTotalAssets(totalAssets);
      setTotalLiabilitiesEquity(totalLiabilitiesEquity);

      setAssets(assetsBalances);
      setLiabilities(liabilitiesEquityBalances);
    } catch (error) {
      console.error('Error fetching opening balances:', error);
      // Don't show error for opening balances - they might not exist yet
      setOpeningBalances([]);
      setAssets([]);
      setLiabilities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const data = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        accounting_method: selectedAccountingMethod.value,
        vat_period: selectedVatPeriod.value,
      };

      await axios.put(getApiUrl(`${companyId}/accounting/fiscalyears/${fiscalYearId}`), data, { withCredentials: true });

      // swal({
      //   title: "Success",
      //   text: "Fiscal year updated successfully",
      //   icon: "success",
      //   button: "OK",
      // });
      toast.success("Fiscal year updated successfully");

      setIsEditing(false);
      fetchFiscalYearDetails();
    } catch (error) {
      // swal({
      //   title: "Error",
      //   text: "Failed to update fiscal year",
      //   icon: "error",
      //   button: "OK",
      // });
      toast.error("Failed to update fiscal year");
    }
  };

  const handleDelete = async () => {
    swal({
      title: "Are you sure?",
      text: "Once deleted the fiscal year cannot be recovered, this will delete all data related to the fiscal year!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await axios.delete(getApiUrl(`${companyId}/accounting/fiscalyears/${fiscalYearId}`), { withCredentials: true });

          toast.success("Fiscal year deleted successfully");

          history.push(`/${companyId}/settings/bookkeeping#rakenskapsar`);
        } catch (error) {
          toast.error("Failed to delete fiscal year");
        }
      }
    });
  };

  const handleDateChange = (setDate) => (date) => {
    const adjustedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    setDate(adjustedDate);
  };

  const saveOpeningBalances = async () => {
    const totalExistingAssets = assets.reduce((acc, balance) => acc + parseFloat(balance.opening_balance || 0), 0);
    const totalExistingLiabilities = liabilities.reduce((acc, balance) => acc + parseFloat(balance.opening_balance || 0), 0);

    const totalNewAssets = newOpeningAssetsBalances.reduce((acc, balance) => acc + parseFloat(balance.opening_balance || 0), 0);
    const totalNewLiabilities = newOpeningLiabilityBalances.reduce((acc, balance) => acc + parseFloat(balance.opening_balance || 0), 0);

    const totalAssets = totalExistingAssets + totalNewAssets;
    const totalLiabilities = totalExistingLiabilities + totalNewLiabilities;

    if (Math.abs(totalAssets) !== Math.abs(totalLiabilities)) {
      // swal({
      //   title: "Error",
      //   text: "Total assets and total liabilities must be equal.",
      //   icon: "error",
      //   button: "OK",
      // });
      toast.error("Total assets and total liabilities must be equal.");
      return;
    }

    try {
      const validOpeningBalances = newOpeningAssetsBalances.concat(newOpeningLiabilityBalances).filter(
        balance => balance.account_id && balance.opening_balance !== 0
      );

      const data = {
        openingBalances: validOpeningBalances,
        deletedBalances: deletedBalances
      };

      const response = await axios.post(getApiUrl(`${companyId}/accounting/fiscalyears/${fiscalYearId}/opening-balances`),
        data,
        { withCredentials: true });

      if (response.data.success) {
        // swal({
        //   title: "Success",
        //   text: "Opening balances saved successfully",
        //   icon: "success",
        //   button: "OK",
        // });
        toast.success("Opening balances saved successfully");

        setNewOpeningAssetsBalances([]);
        setNewOpeningLiabilityBalances([]);
        setDeletedBalances([]);

        fetchOpeningBalances();
      }
    } catch (error) {
      // swal({
      //   title: "Error",
      //   text: "Failed to save opening balances",
      //   icon: "error",
      //   button: "OK",
      //});
      toast.error("Failed to save opening balances");
    }
  };

  const removeFetchedAssetBalance = (balance, index) => {
    const updatedAssets = assets.filter((_, i) => i !== index);
    setAssets(updatedAssets);
    const totalAssets = updatedAssets.reduce((acc, balance) => acc + parseFloat(balance.opening_balance || 0), 0);
    setTotalAssets(totalAssets + newOpeningAssetsBalances.reduce((acc, balance) => acc + parseFloat(balance.opening_balance || 0), 0));
    setDeletedBalances([...deletedBalances, balance.account_id]);
  };

  const handleAssetAccountChange = (index, selectedOption) => {
    const updatedBalances = [...newOpeningAssetsBalances];
    const [account_number, account_name] = selectedOption.label.split(' - ');
    updatedBalances[index].account_id = selectedOption.value;
    updatedBalances[index].account_name = account_name;
    updatedBalances[index].account_number = account_number;
    setNewOpeningAssetsBalances(updatedBalances);
  };

  const handleAssetBalanceChange = (index, event) => {
    const updatedBalances = [...newOpeningAssetsBalances];
    updatedBalances[index].opening_balance = event.target.value;
    setNewOpeningAssetsBalances(updatedBalances);
    const totalNewAssets = updatedBalances.reduce((acc, balance) => acc + parseFloat(balance.opening_balance || 0), 0);
    const totalAssets = totalNewAssets + assets.reduce((acc, balance) => acc + parseFloat(balance.opening_balance || 0), 0);
    setTotalAssets(totalAssets);
  };

  const removeNewAssetBalanceRow = (index) => {
    const updatedBalances = newOpeningAssetsBalances.filter((_, i) => i !== index);
    setNewOpeningAssetsBalances(updatedBalances);
  };

  const addNewAssetsBalanceRow = () => {
    setNewOpeningAssetsBalances([...newOpeningAssetsBalances, { account_id: '', account_name: '', opening_balance: 0 }]);
  };

  const removeFetchedLiabilityBalance = (balance, index) => {
    const updatedLiabilities = liabilities.filter((_, i) => i !== index);
    setLiabilities(updatedLiabilities);
    const totalLiabilities = updatedLiabilities.reduce((acc, balance) => acc + parseFloat(balance.opening_balance || 0), 0);
    setTotalLiabilitiesEquity(totalLiabilities + newOpeningLiabilityBalances.reduce((acc, balance) => acc + parseFloat(balance.opening_balance || 0), 0));
    setDeletedBalances([...deletedBalances, balance.account_id]);
  };

  const handleLiabilityAccountChange = (index, selectedOption) => {
    const updatedBalances = [...newOpeningLiabilityBalances];
    const [account_number, account_name] = selectedOption.label.split(' - ');
    updatedBalances[index].account_id = selectedOption.value;
    updatedBalances[index].account_name = account_name;
    updatedBalances[index].account_number = account_number;
    setNewOpeningLiabilityBalances(updatedBalances);
  };

  const handleLiabilityBalanceChange = (index, event) => {
    const updatedBalances = [...newOpeningLiabilityBalances];
    updatedBalances[index].opening_balance = event.target.value;
    setNewOpeningLiabilityBalances(updatedBalances);
    const totalNewLiabilities = updatedBalances.reduce((acc, balance) => acc + parseFloat(balance.opening_balance || 0), 0);
    const totalLiabilities = totalNewLiabilities + liabilities.reduce((acc, balance) => acc + parseFloat(balance.opening_balance || 0), 0);
    setTotalLiabilitiesEquity(totalLiabilities);
  };

  const removeNewLiabilityBalanceRow = (index) => {
    const updatedBalances = newOpeningLiabilityBalances.filter((_, i) => i !== index);
    setNewOpeningLiabilityBalances(updatedBalances);
  };

  const addNewLiabilityBalanceRow = () => {
    setNewOpeningLiabilityBalances([...newOpeningLiabilityBalances, { account_id: '', account_name: '', opening_balance: 0 }]);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Laddar räkenskapsårsdetaljer...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .modern-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          transition: all 0.3s ease;
          margin-bottom: 2rem;
          overflow: hidden;
        }
        
        .modern-card:hover {
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
          transform: translateY(-2px);
        }
        
        .modern-card-header {
          background: #f8fafc;
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .modern-card-body {
          padding: 2rem;
        }
        
        .section-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .section-subtitle {
          color: #64748b;
          font-size: 0.875rem;
          margin: 0;
          margin-top: 0.25rem;
        }
        
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 500;
        }
        
        .status-badge.open {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          color: #166534;
        }
        
        .status-badge.closed {
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          color: #475569;
        }
        
        .action-buttons {
          display: flex;
          gap: 0.75rem;
          align-items: center;
        }
        
        .modern-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          transition: all 0.3s ease;
          cursor: pointer;
          text-decoration: none;
        }
        
        .modern-button.primary {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
        }
        
        .modern-button.primary:hover {
          background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        
        .modern-button.secondary {
          background: #f1f5f9;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        
        .modern-button.secondary:hover {
          background: #e2e8f0;
          color: #475569;
        }
        
        .modern-button.danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }
        
        .modern-button.danger:hover {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
        }
        
        .modern-button.success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }
        
        .modern-button.success:hover {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }
        
        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          margin-bottom: 2rem;
        }
        
        .form-group {
          margin-bottom: 1.5rem;
        }
        
        .form-label {
          display: block;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }
        
        .form-control-modern {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          background: white;
        }
        
        .form-control-modern:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .date-input-wrapper {
          position: relative;
        }
        
        .date-input-wrapper .react-datepicker-wrapper {
          width: 100%;
        }
        
        .date-input-wrapper .form-control {
          padding-right: 3rem;
        }
        
        .date-icon {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          pointer-events: none;
        }
        
        .balance-table {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          margin-bottom: 2rem;
        }
        
        .balance-table table {
          width: 100%;
          margin: 0;
        }
        
        .balance-table thead {
          background: #f8fafc;
        }
        
        .balance-table th {
          padding: 1rem 1.5rem;
          font-weight: 600;
          color: #374151;
          font-size: 0.875rem;
          border: none;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }
        
        .balance-table td {
          padding: 1rem 1.5rem;
          border: none;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;
        }
        
        .balance-table tbody tr:hover {
          background: #f8fafc;
        }
        
        .balance-table tbody tr:last-child td {
          border-bottom: none;
        }
        
        .total-row {
          background: #f1f5f9 !important;
          font-weight: 600;
        }
        
        .total-row td {
          border-top: 2px solid #e2e8f0 !important;
          border-bottom: none !important;
        }
        
        .balance-summary {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-radius: 12px;
          padding: 2rem;
          margin-top: 2rem;
          border: 1px solid #e2e8f0;
        }
        
        .balance-difference {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: white;
          border-radius: 8px;
          border: 1px solid #fecaca;
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          color: #dc2626;
          font-weight: 600;
          margin-top: 1rem;
        }
        
        .balance-difference.balanced {
          border-color: #bbf7d0;
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          color: #166534;
        }
        
        .add-row-button {
          color: #3b82f6;
          text-decoration: none;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          transition: all 0.3s ease;
          margin: 1rem 0;
        }
        
        .add-row-button:hover {
          background: #f3f4f6;
          text-decoration: none;
          color: #1d4ed8;
        }
        
        .metric-card {
          background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
          border-radius: 12px;
          padding: 1.5rem;
          border: 1px solid #e2e8f0;
          text-align: center;
          transition: all 0.3s ease;
        }
        
        .metric-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }
        
        .metric-value {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }
        
        .metric-label {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }
        
        .info-alert {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border: 1px solid #93c5fd;
          border-radius: 12px;
          padding: 1.5rem;
          margin: 2rem 0;
          display: flex;
          align-items: start;
          gap: 1rem;
        }
        
        .info-alert-icon {
          color: #3b82f6;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .info-alert-content h6 {
          color: #1e40af;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        
        .info-alert-content p {
          color: #1e40af;
          margin: 0;
          font-size: 0.875rem;
        }
      `}</style>

      <PageTitle activeMenu="Räkenskapsår" motherMenu="Bokföring" />

      <div className='col-xl-12'>
        {/* Header Card */}
        <div className="modern-card">
          <div className="modern-card-header">
            <div>
              <h1 className="section-title">
                <Calendar size={24} />
                Räkenskapsår {fiscalYear.year}
              </h1>
              <p className="section-subtitle">
                Hantera räkenskapsårets inställningar och ingångsvärden
              </p>
            </div>
            <div className="action-buttons">
              <div className={`status-badge ${fiscalYear.state === 'open' ? 'open' : 'closed'}`}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: fiscalYear.state === 'open' ? '#10b981' : '#64748b'
                }} />
                {fiscalYear.state === 'open' ? 'Öppen' : 'Stängd'}
              </div>
              {!isEditing ? (
                <>
                  <button
                    className="modern-button secondary"
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit size={16} />
                    Redigera
                  </button>
                  <button
                    className="modern-button danger"
                    onClick={handleDelete}
                  >
                    <Trash2 size={16} />
                    Radera
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="modern-button secondary"
                    onClick={() => setIsEditing(false)}
                  >
                    Avbryt
                  </button>
                  <button
                    className="modern-button primary"
                    onClick={handleSave}
                  >
                    <Save size={16} />
                    Spara
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="modern-card-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Startdatum</label>
                <div className="date-input-wrapper">
                  <DatePicker
                    selected={startDate}
                    onChange={handleDateChange(setStartDate)}
                    dateFormat="yyyy-MM-dd"
                    className="form-control-modern"
                    locale={sv}
                    disabled={!isEditing}
                  />
                  <Calendar className="date-icon" size={16} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Slutdatum</label>
                <div className="date-input-wrapper">
                  <DatePicker
                    selected={endDate}
                    onChange={handleDateChange(setEndDate)}
                    dateFormat="yyyy-MM-dd"
                    className="form-control-modern"
                    locale={sv}
                    disabled={!isEditing}
                  />
                  <Calendar className="date-icon" size={16} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Redovisningsmetod
                  <Info
                    size={14}
                    className="ms-1 text-muted"
                    data-tooltip-id="redovisningsmetod"
                    data-tooltip-content="Du kan se vilken metod du har på verksamt.se"
                  />
                </label>
                <Select
                  options={accountingMethodOptions}
                  value={selectedAccountingMethod}
                  onChange={setSelectedAccountingMethod}
                  isDisabled={!isEditing}
                  styles={{
                    control: (base) => ({
                      ...base,
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      padding: '0.25rem',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#3b82f6'
                      }
                    })
                  }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Momsperiod
                  <Info
                    size={14}
                    className="ms-1 text-muted"
                    data-tooltip-id="momsperiod"
                    data-tooltip-content="Du kan se vilken momsperiod du har på verksamt.se"
                  />
                </label>
                <Select
                  options={vatPeriodOptions}
                  value={selectedVatPeriod}
                  onChange={setSelectedVatPeriod}
                  isDisabled={!isEditing}
                  styles={{
                    control: (base) => ({
                      ...base,
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      padding: '0.25rem',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#3b82f6'
                      }
                    })
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Opening Balances Card */}
        <div className="modern-card">
          <div className="modern-card-header">
            <div>
              <h2 className="section-title">
                <TrendingUp size={24} />
                Ingående balans
              </h2>
              <p className="section-subtitle">
                Ange ingående värden för tillgångar och skulder
              </p>
            </div>
          </div>

          <div className="modern-card-body">
            {/* Provisional IB banner */}
            {isProvisional && (
              <div className="info-alert" style={{ marginTop: 0, marginBottom: '1.5rem' }}>
                <AlertCircle className="info-alert-icon" size={20} />
                <div className="info-alert-content">
                  <h6>Preliminära ingående balanser</h6>
                  <p>
                    Ingående balanser beräknas dynamiskt från föregående år som ännu inte är stängt.
                    De fastställs när föregående år stängs.
                  </p>
                </div>
              </div>
            )}

            {/* Balance Summary */}
            <div className="row mb-4">
              <div className="col-md-4">
                <div className="metric-card">
                  <div className="metric-value" style={{ color: '#059669' }}>
                    {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(totalAssets)}
                  </div>
                  <div className="metric-label">Totala tillgångar</div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="metric-card">
                  <div className="metric-value" style={{ color: '#dc2626' }}>
                    {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(Math.abs(totalLiabilitiesEquity))}
                  </div>
                  <div className="metric-label">Skulder & Eget kapital</div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="metric-card">
                  <div className="metric-value" style={{
                    color: Math.abs(totalAssets) === Math.abs(totalLiabilitiesEquity) ? '#059669' : '#dc2626'
                  }}>
                    {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(totalAssets - Math.abs(totalLiabilitiesEquity))}
                  </div>
                  <div className="metric-label">Differens</div>
                </div>
              </div>
            </div>

            {/* Assets Table */}
            <h3 className="h5 fw-semibold mb-3" style={{ color: '#1e293b' }}>Tillgångar</h3>
            <div className="balance-table">
              <table>
                <thead>
                  <tr>
                    <th>Kontonummer</th>
                    <th>Kontonamn</th>
                    <th>Ingångssaldo</th>
                    <th style={{ width: '80px' }}>Åtgärder</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map((balance, index) => (
                    <tr key={index}>
                      <td className="fw-semibold">{balance.account_number}</td>
                      <td>{balance.account_name}</td>
                      <td className="fw-semibold">
                        {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(balance.opening_balance)}
                      </td>
                      <td>
                        <button
                          className="btn btn-link text-danger p-0"
                          onClick={() => removeFetchedAssetBalance(balance, index)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {newOpeningAssetsBalances.map((balance, index) => (
                    <tr key={`new-${index}`}>
                      <td>
                        <Select
                          options={assetsAccountsOptions}
                          value={assetsAccountsOptions.find(option => option.value === balance.account_id)}
                          onChange={(selectedOption) => handleAssetAccountChange(index, selectedOption)}
                          placeholder="Välj konto..."
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '38px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px'
                            })
                          }}
                        />
                      </td>
                      <td className="text-muted">{balance.account_name}</td>
                      <td>
                        <input
                          type="number"
                          className="form-control"
                          value={balance.opening_balance}
                          onChange={(event) => handleAssetBalanceChange(index, event)}
                          placeholder="0.00"
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-link text-danger p-0"
                          onClick={() => removeNewAssetBalanceRow(index)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td colSpan="2" className="fw-bold">Summa tillgångar</td>
                    <td className="fw-bold">
                      {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(totalAssets)}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <button className="add-row-button" onClick={addNewAssetsBalanceRow}>
              <Plus size={16} />
              Lägg till ny tillgång
            </button>

            {/* Liabilities Table */}
            <h3 className="h5 fw-semibold mb-3 mt-5" style={{ color: '#1e293b' }}>Skulder och Eget kapital</h3>
            <div className="balance-table">
              <table>
                <thead>
                  <tr>
                    <th>Kontonummer</th>
                    <th>Kontonamn</th>
                    <th>Ingångssaldo</th>
                    <th style={{ width: '80px' }}>Åtgärder</th>
                  </tr>
                </thead>
                <tbody>
                  {liabilities.map((balance, index) => (
                    <tr key={index}>
                      <td className="fw-semibold">{balance.account_number}</td>
                      <td>{balance.account_name}</td>
                      <td className="fw-semibold">
                        {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(balance.opening_balance)}
                      </td>
                      <td>
                        <button
                          className="btn btn-link text-danger p-0"
                          onClick={() => removeFetchedLiabilityBalance(balance, index)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {newOpeningLiabilityBalances.map((balance, index) => (
                    <tr key={`new-${index}`}>
                      <td>
                        <Select
                          options={liabilityAccountsOptions}
                          value={liabilityAccountsOptions.find(option => option.value === balance.account_id)}
                          onChange={(selectedOption) => handleLiabilityAccountChange(index, selectedOption)}
                          placeholder="Välj konto..."
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '38px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px'
                            })
                          }}
                        />
                      </td>
                      <td className="text-muted">{balance.account_name}</td>
                      <td>
                        <input
                          type="number"
                          className="form-control"
                          value={balance.opening_balance}
                          onChange={(event) => handleLiabilityBalanceChange(index, event)}
                          placeholder="0.00"
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-link text-danger p-0"
                          onClick={() => removeNewLiabilityBalanceRow(index)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td colSpan="2" className="fw-bold">Summa skulder och eget kapital</td>
                    <td className="fw-bold">
                      {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(totalLiabilitiesEquity)}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <button className="add-row-button" onClick={addNewLiabilityBalanceRow}>
              <Plus size={16} />
              Lägg till skuld/eget kapital
            </button>

            {/* Balance Validation */}
            {Math.abs(totalAssets) !== Math.abs(totalLiabilitiesEquity) ? (
              <div className="balance-difference">
                <div>
                  <AlertCircle size={20} className="me-2" />
                  <strong>Balansen stämmer inte!</strong>
                </div>
                <div>
                  Differens: {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(totalAssets - Math.abs(totalLiabilitiesEquity))}
                </div>
              </div>
            ) : (
              <div className="balance-difference balanced">
                <div>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: '#10b981',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '0.5rem'
                  }}>
                    ✓
                  </div>
                  <strong>Balansen stämmer!</strong>
                </div>
                <div>Tillgångar = Skulder + Eget kapital</div>
              </div>
            )}

            <div className="d-flex justify-content-end mt-4">
              <button
                className="modern-button success"
                onClick={saveOpeningBalances}
                disabled={Math.abs(totalAssets) !== Math.abs(totalLiabilitiesEquity)}
              >
                <Save size={16} />
                Spara ingångsbalanser
              </button>
            </div>
          </div>
        </div>

        {/* Info Alert */}
        <div className="info-alert">
          <Info className="info-alert-icon" size={20} />
          <div className="info-alert-content">
            <h6>Viktigt att komma ihåg</h6>
            <p>
              Ingångsbalansen måste alltid balansera - summan av tillgångar ska vara lika med summan av skulder och eget kapital.
              Detta är en grundläggande princip inom dubbel bokföring som säkerställer att balansräkningen stämmer.
            </p>
          </div>
        </div>
      </div>

      <ReactTooltip place="right" effect="solid" id="redovisningsmetod" />
      <ReactTooltip place="right" effect="solid" id="momsperiod" />
    </>
  );
};

export default FiscalYearDetails;
